import { FileSystemErrorCodes, MessageFlags, MessageType } from 'app/modules/remote-control-ng/definitions';
import { MemoryBuffer } from '../../utils/memoryBuffer';
import { BaseMessage } from '../baseMessage';
import { TimeHelpers } from '../../utils/time';
import { MathHelpers } from '../../utils/math';
import { FileData } from 'app/modules/remote-control/file-manager/fileData';
import { RequestReplyService } from '../../services/requestReplyService';

const MAX_PATH = 260; // maximum length of a path in WIN32_FIND_DATA

export interface IDosFileTreeResponse {
    tree: any[],
    done: boolean
}

class DosFileTreeResponse implements IDosFileTreeResponse {
    public tree: any[] = [];
    public done: boolean = false;
}

export interface IFileSystemItem {
    title: string;
    data: FileData;
    children: boolean;
    type: string;
    collapsed?: boolean;
    symlink?: boolean;
    path?: string;
}

/** PC_DOS_DIR MessageType */
export class DosGetDirMessage extends BaseMessage {
    private dir: string;
    private dirOnly: number;
    public response: IDosFileTreeResponse;

    constructor(dir?: string, dirOnly?: boolean) {
        super();
        this.dir = dir;
        this.dirOnly = !dirOnly ? 0 : 1;
        this.header.command = MessageType.PC_DOS_DIR;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.dir);
        buffer.writeUInt8(this.dirOnly);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        var count = ((this.header.size - 8) / 560) | 0;
        this.response = new DosFileTreeResponse();
        for (var files = 0; files < count; files++) {
            const attributes = buffer.readUInt32LE();

            const creationLow = buffer.readUInt32LE();
            const creationHigh = buffer.readUInt32LE();
            const accessLow = buffer.readUInt32LE();
            const accessHigh = buffer.readUInt32LE();

            const writeLow = buffer.readUInt32LE();
            const writeHigh = buffer.readUInt32LE();
            const fileSizeHigh = buffer.readUInt32LE();
            const fileSizeLow = buffer.readUInt32LE();
            const fileSize = MemoryBuffer.makeBigInteger(fileSizeHigh, fileSizeLow);

            const reservedLow = buffer.readUInt32LE();
            const fileName = buffer.readCharArray(MAX_PATH * 2);

            const nodeText = fileName;
            const accessDate = TimeHelpers.convert64bitTimestampToDate(writeHigh, writeLow);
            const formattedDate = (writeHigh === 0 && writeLow === 0) ? "" : accessDate.toDateString();
            // fullPath done in handler
            const file = {
                title: nodeText,
                data: {
                    fileName: fileName,
                    fullPath: '',
                    fileSize: MathHelpers.toSizeString(fileSize),
                    date: accessDate,
                    formattedDate: formattedDate
                } as FileData,
                children: ((attributes & 0x10) != 0),
                type: ((attributes & 0x10) != 0) ? "folder" : "file",
                collapsed: true
            } as IFileSystemItem;
            this.response.tree[files] = file;
            this.response.done = false;
        }
    }
}


/** PC_QUERY_DEVICE MessageType */
export class DosQueryDeviceMessage extends BaseMessage {
    private subCmd: number;
    public drives: IFileSystemItem[];
    private requestReplyService: RequestReplyService;

    constructor(subCmd?: number) {
        super();
        this.subCmd = subCmd;
        this.header.command = MessageType.PC_QUERY_DEVICE;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeUInt8(this.subCmd);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode === 0 && this.header.size - buffer.getPosition() > 0) {
            const driveLetters = buffer.readString();
            const driveTypes = buffer.readString();
            const drivesArray = driveLetters.split("");
            // const driveTypesArray = driveTypes.split("");
            this.drives = [];
            if (drivesArray?.length > 0) {
                for (let i = 0; i < drivesArray.length; i++) {
                    this.drives[i] = {
                        title: drivesArray[i],
                        data: {
                            fileName: drivesArray[i] + ":",
                            fullPath: drivesArray[i] + ":",
                            fileSize: "0",
                            date: new Date()
                        } as FileData,
                        children: true,
                        type: "drive"
                    } as IFileSystemItem;
                }
            }
        }
    }
}

/** PC_DOS_SEARCH MessageType */
export class DosSearchMessage extends BaseMessage {
    private search: string;
    private dir: string;
    public response: IDosFileTreeResponse;

    constructor(searchString?: string, dir?: string) {
        super();
        this.search = searchString;
        this.dir = dir;
        this.header.command = MessageType.PC_DOS_SEARCH;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.dir);
        buffer.writeStringLE(this.search);
        buffer.writeUInt8(1);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        this.response = new DosFileTreeResponse();
        this.response.tree = [];

        if (this.header.size > 9) {
            var curFile = 0;
            var pathCount = buffer.readUInt32BE();
            for (var paths = 0; paths < pathCount; paths++) {
                var path = "";
                if (this.header.command == MessageType.PC_DOS_SEARCH) {
                    path = buffer.readString();
                    path = path.replace(/\\/g, '/'); // Fix windows paths
                    if (path[path.length - 1] != '/') {
                        path = path.concat('/'); // Fix path terminations
                    }
                }
                var count = buffer.readUInt32BE();
                for (var files = 0; files < count; files++) {
                    var attributes = buffer.readUInt32LE();
                    const creationLow = buffer.readUInt32LE();
                    const creationHigh = buffer.readUInt32LE();

                    const accessLow = buffer.readUInt32LE();
                    const accessHigh = buffer.readUInt32LE();

                    const writeLow = buffer.readUInt32LE();
                    const writeHigh = buffer.readUInt32LE();

                    const fileSizeHigh = buffer.readUInt32LE();
                    const fileSizeLow = buffer.readUInt32LE();

                    const reservedLow = buffer.readUInt32LE();

                    const fileName = buffer.readCharArray(MAX_PATH * 2);
                    const fileSize = MemoryBuffer.makeBigInteger(fileSizeHigh, fileSizeLow);

                    const nodeText = fileName;
                    const accessDate = TimeHelpers.convert64bitTimestampToDate(writeHigh, writeLow);
                    const formattedDate = (writeHigh === 0 && writeLow === 0) ? "" : accessDate.toDateString();
                    const fullPath = path + fileName;

                    this.response.tree[curFile++] = {
                        title: nodeText,
                        data: {
                            fileName: fileName,
                            fullPath: fullPath,
                            fileSize: MathHelpers.toSizeString(fileSize),
                            date: accessDate,
                            formattedDate: formattedDate
                        } as FileData,
                        children: ((attributes & 0x10) != 0),
                        type: ((attributes & 0x10) != 0) ? "folder" : "file",
                        symlink: ((attributes & 0x04) != 0),
                        path: path
                    } as IFileSystemItem;
                }
            }
        }

        this.response.done = (this.header.flags & MessageFlags.IS_ZIPCOMPRESSED) === 0x0;
    }
}

/** PC_CANCEL_ASYNC MessageType request */
export class CancelAsyncMessage extends BaseMessage {
    constructor(reqIdToCancel: number) {
        super();
        this.header.requestId = reqIdToCancel;
        this.header.flags = MessageFlags.HAS_REQID;
        this.header.command = MessageType.PC_CANCEL_ASYNC;
    }
}

/** PC_DOS_GET_FILE MessageType */
export class DosGetFileMessage extends BaseMessage {
    private filepath: string;
    private blocksize: number;
    public fileSize: number;

    constructor(filepath?: string, blocksize?: number) {
        super();
        this.filepath = filepath;
        this.blocksize = blocksize;
        this.header.command = MessageType.PC_DOS_GET_FILE;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.filepath);
        buffer.writeUInt32BE(this.blocksize);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode == 0) {
            this.fileSize = buffer.readUInt32BE();
        }
    }
}

/** PC_DOS_RENAME MessageType */
export class DosRenameFileMessage extends BaseMessage {
    private origName: string;
    private newName: string;
    private blocksize: number;

    constructor(origName?: string, newName?: string, blocksize?: number) {
        super();
        this.origName = origName;
        this.newName = newName;
        this.blocksize = blocksize;
        this.header.command = MessageType.PC_DOS_RENAME;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.origName);
        buffer.writeStringLE(this.newName);
        buffer.writeUInt32BE(this.blocksize);
    }
}

/** PC_DOS_DEL MessageType */
export class DosDeleteFileMessage extends BaseMessage {
    private filepath: string;

    constructor(filepath?: string) {
        super();
        this.filepath = filepath;
        this.header.command = MessageType.PC_DOS_DEL;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.filepath);
    }
}

/** PC_DOS_RMDIR MessageType */
export class DosDeleteFolderMessage extends BaseMessage {
    private filepath: string;

    constructor(filepath?: string) {
        super();
        this.filepath = filepath;
        this.header.command = MessageType.PC_DOS_RMDIR;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.filepath);
    }
}

/** PC_DOS_COMPRESSION MessageType */
export class DosCompressItemMessage extends BaseMessage {
    private filepath: string;
    private compressCommand: number;

    public fileName: string;

    constructor(filepath?: string, command?: number) {
        super();
        this.header.command = MessageType.PC_DOS_COMPRESSION;
        this.filepath = filepath;
        this.compressCommand = command;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeUInt8(this.compressCommand);
        buffer.writeStringLE(this.filepath);
    }

    public deserializeBody(buffer: MemoryBuffer): void {
        if (this.header.errorCode === 0) {
            // Reading the first byte as a command response type
            // could be used in the future implementation for the decompression
            buffer.readInt8();
            this.fileName = buffer.readString();
        }
    }
}

/** PC_DOS_EMPTY_FOLDER MessageType */
export class DosEmptyFolderMessage extends BaseMessage {
    private filepath: string;

    constructor(filepath?: string) {
        super();
        this.filepath = filepath;
        this.header.command = MessageType.PC_DOS_EMPTY_FOLDER;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.filepath);
    }
}

export interface IDosGetFileDataResponse {
    pos: number,            // get position in memory buffer
    dataLength: number,
    offsetArray: Uint8Array     // creates offsetArray var and starts from pos defined above extending to the end of the data
}

class DosGetFileDataResponse implements IDosGetFileDataResponse {
    public pos: number = 0;
    public dataLength: number = 0;
    offsetArray: Uint8Array;
}

/** PC_DOS_GET_FILE_DATA MessageType */
export class DosGetFileDataMessage extends BaseMessage {
    private offset: number;
    private blocksize: number;

    public response: IDosGetFileDataResponse;
    constructor(offset?: number, blocksize?: number) {
        super();
        this.offset = offset;
        this.blocksize = blocksize;
        this.header.command = MessageType.PC_DOS_GET_FILE_DATA;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeUInt32BE(this.offset);
        buffer.writeUInt32BE(this.blocksize);
    }
    public deserializeBody(buffer: MemoryBuffer): void {

        if (this.header.errorCode === 0) {
            this.response = new DosGetFileDataResponse();
            // TODO 2025.1: Regarding fileArray, rather then adding checking to see if it can fit more
            // and creating a new array if it cannot. Try to see if we can implement an array list instead
            this.response.pos = buffer.getPosition(); // get position in memory buffer
            const data = buffer.getByteArray();
            this.response.dataLength = data.length;
            this.response.offsetArray = data.subarray(this.response.pos, this.response.dataLength);
            // creates offsetArray var and starts from pos defined above extending to the end of the data
        }
    }
}

/** PC_DOS_MKDIR MessageType */
export class DosMakeDirectoryMessage extends BaseMessage {
    private directory: string;
    private requestReplyService: RequestReplyService;

    constructor(newDirectoryPath?: string) {
        super();
        this.directory = newDirectoryPath;
        this.header.command = MessageType.PC_DOS_MKDIR;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.directory);
    }
}

/** PC_DOS_SEND_FILE MessageType */
export class DosSendFileMessage extends BaseMessage {
    private filePath: string;
    private fileSize: number;
    private blockSize: number;
    private requestReplyService: RequestReplyService;

    constructor(filePath?: string, fileSize?: number, blockSize?: number) {
        super();
        this.filePath = filePath;
        this.fileSize = fileSize;
        this.blockSize = blockSize;
        this.header.command = MessageType.PC_DOS_SEND_FILE;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.writeStringLE(this.filePath);
        buffer.writeUInt32BE(this.fileSize);
        buffer.writeUInt32BE(this.blockSize);
    }
}

/** PC_DOS_SEND_FILE_DATA MessageType */
export class DosSendFileDataMessage extends BaseMessage {
    private fileChunk: ArrayBuffer|Uint8Array|number[];
    private requestReplyService: RequestReplyService;

    constructor(fileChunk?: ArrayBuffer|Uint8Array|number[]) {
        super();
        this.fileChunk = fileChunk;
        this.header.command = MessageType.PC_DOS_SEND_FILE_DATA;
    }

    public serializeBody(buffer: MemoryBuffer) {
        buffer.concatByteArray(this.fileChunk);
    }
}
